/**
 * AdminListView
 *
 * Reusable table view for every Aether admin tab (Bestellungen, E-Mails,
 * Affiliates, Rabatte, Lizenzen). Mirrors the Pollnick-Create UX the user
 * referenced: a unified search/filter bar, a settings cog with row-size +
 * column-visibility + group-by-status controls, and an optional table-style
 * body that can be grouped by status.
 *
 * All view-state (size, visible columns, grouping) is persisted to
 * localStorage under `aether_list_<storageKey>` so each tab remembers how
 * the user left it.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Search, Settings, RotateCcw, Lock, ChevronDown, ChevronRight } from 'lucide-react';

const ROW_SIZES = {
  mini:     { py: 'py-1.5',  text: 'text-[11px]', icon: 12 },
  compact:  { py: 'py-2.5',  text: 'text-xs',     icon: 14 },
  standard: { py: 'py-3.5',  text: 'text-sm',     icon: 16 },
};

function loadSettings(storageKey, fallback) {
  if (!storageKey) return fallback;
  try {
    const raw = localStorage.getItem(`aether_list_${storageKey}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return { ...fallback, ...parsed };
  } catch { return fallback; }
}

function saveSettings(storageKey, settings) {
  if (!storageKey) return;
  try {
    localStorage.setItem(`aether_list_${storageKey}`, JSON.stringify(settings));
  } catch { /* ignore */ }
}

export default function AdminListView({
  storageKey,           // e.g. 'orders' — settings persistence key
  data = [],            // array of items
  columns,              // [{ id, label, render(item), locked?, mono?, align?, width? }]
  rowKey,               // (item) => unique key
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Suchen…',
  searchFields,         // optional: which fields to match for search. If unset, parent does the filtering.
  filters,              // optional [{ id, label, value, onChange, options: [{ value, label }] }]
  groupByStatus = false,// allow grouping; toggled via cog
  groupKey,             // (item) => string (only used when groupByStatus is on)
  groupLabels,          // { [statusKey]: 'Display label' }
  rowActions,           // (item) => ReactNode trailing buttons cell
  emptyText = 'Keine Einträge.',
  emptyIcon: EmptyIcon,
  onRowClick,
}) {
  const defaultSettings = useMemo(() => ({
    size: 'compact',
    visibleCols: columns.filter(c => c.locked || c.defaultVisible !== false).map(c => c.id),
    grouped: groupByStatus,
  }), [columns, groupByStatus]);

  const [settings, setSettings] = useState(() => loadSettings(storageKey, defaultSettings));
  useEffect(() => { saveSettings(storageKey, settings); }, [storageKey, settings]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onClick = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [settingsOpen]);

  const sizeCfg = ROW_SIZES[settings.size] || ROW_SIZES.compact;

  // Apply built-in search filter if searchFields is provided. Otherwise the
  // parent component already filtered `data`.
  const filtered = useMemo(() => {
    if (!searchFields || !searchValue) return data;
    const q = searchValue.trim().toLowerCase();
    if (!q) return data;
    return data.filter((item) =>
      searchFields.some(f => String(item[f] ?? '').toLowerCase().includes(q))
    );
  }, [data, searchFields, searchValue]);

  const visibleColumns = useMemo(
    () => columns.filter(c => c.locked || settings.visibleCols.includes(c.id)),
    [columns, settings.visibleCols],
  );

  // Optionally group items by their status field.
  const groups = useMemo(() => {
    if (!settings.grouped || !groupKey) {
      return [{ key: '__all__', label: null, items: filtered }];
    }
    const map = new Map();
    for (const it of filtered) {
      const k = String(groupKey(it) ?? '—');
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    return Array.from(map.entries()).map(([k, items]) => ({
      key: k,
      label: groupLabels?.[k] || k,
      items,
    }));
  }, [filtered, settings.grouped, groupKey, groupLabels]);

  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleGroup = (gk) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(gk)) next.delete(gk);
      else next.add(gk);
      return next;
    });
  };

  const toggleColumn = (id) => {
    setSettings(s => ({
      ...s,
      visibleCols: s.visibleCols.includes(id)
        ? s.visibleCols.filter(c => c !== id)
        : [...s.visibleCols, id],
    }));
  };

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="monolith-glass flex items-center px-3 py-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-white/30 mr-2 shrink-0" />
          <input
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={searchPlaceholder}
            className="bg-transparent border-none outline-none font-mono text-[11px] w-full text-white placeholder:text-white/20"
          />
        </div>

        {/* Custom filter dropdowns */}
        {(filters || []).map((f) => (
          <select
            key={f.id}
            value={f.value}
            onChange={(e) => f.onChange(e.target.value)}
            className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 font-mono text-[10px] text-white outline-none cursor-pointer"
            style={{ colorScheme: 'dark' }}
            title={f.label}
          >
            {(f.options || []).map((o) => (
              <option key={o.value} value={o.value} style={{ background: '#1a1a1a', color: '#fff' }}>{o.label}</option>
            ))}
          </select>
        ))}

        {/* Settings cog */}
        <div className="relative" ref={settingsRef}>
          <button
            onClick={() => setSettingsOpen(s => !s)}
            className={`p-2 rounded-lg liquid-glass hover:bg-white/10 ${settingsOpen ? 'text-white bg-white/10' : 'text-white/60 hover:text-white'}`}
            title="Ansichtseinstellungen"
            data-testid={`list-settings-${storageKey || 'default'}`}
          >
            <Settings size={14} />
          </button>
          {settingsOpen && (
            <div
              className="absolute right-0 top-full mt-2 z-50 rounded-xl shadow-2xl overflow-hidden"
              style={{ background: '#0e0e10', border: '1px solid rgba(255,255,255,0.1)', minWidth: 240 }}
            >
              {/* Row size */}
              <div className="px-4 pt-4 pb-3">
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-2">Zeilen-Größe</p>
                <div className="flex gap-1">
                  {['mini', 'compact', 'standard'].map((s) => (
                    <button
                      key={s}
                      onClick={() => setSettings(prev => ({ ...prev, size: s }))}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest transition-colors ${
                        settings.size === s
                          ? 'bg-orange-500 text-black'
                          : 'bg-white/[0.03] text-white/60 hover:text-white hover:bg-white/[0.06]'
                      }`}
                    >
                      {s === 'mini' ? 'Mini' : s === 'compact' ? 'Kompakt' : 'Standard'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Group by status */}
              {groupByStatus && groupKey && (
                <div className="px-4 py-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-2">Ansicht</p>
                  <label className="flex items-center gap-2 text-[11px] text-white/80 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!settings.grouped}
                      onChange={(e) => setSettings(prev => ({ ...prev, grouped: e.target.checked }))}
                      className="accent-orange-500"
                    />
                    Nach Status gruppieren
                  </label>
                </div>
              )}

              {/* Column visibility */}
              <div className="px-4 py-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-2">Spalten anzeigen</p>
                <div className="space-y-1.5">
                  {columns.map((c) => {
                    const checked = c.locked || settings.visibleCols.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-2 text-[11px] text-white/80 select-none ${c.locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={c.locked}
                          onChange={() => !c.locked && toggleColumn(c.id)}
                          className="accent-orange-500"
                        />
                        <span className="flex-1">{c.label}</span>
                        {c.locked && <Lock size={10} className="text-white/30" />}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Reset */}
              <button
                onClick={() => setSettings(defaultSettings)}
                className="w-full px-4 py-3 text-left text-[11px] font-mono text-orange-400 hover:bg-white/[0.04] border-t flex items-center gap-2"
                style={{ borderColor: 'rgba(255,255,255,0.06)' }}
              >
                <RotateCcw size={11} />
                Standard wiederherstellen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`text-left text-[9px] uppercase tracking-widest text-white/40 border-b ${sizeCfg.py}`} style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                {visibleColumns.map((c) => (
                  <th
                    key={c.id}
                    className={`px-4 ${sizeCfg.py} font-medium ${c.align === 'right' ? 'text-right' : ''}`}
                    style={c.width ? { width: c.width } : undefined}
                  >
                    {c.label}
                  </th>
                ))}
                {rowActions && <th className={`px-4 ${sizeCfg.py} font-medium text-right`}></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + (rowActions ? 1 : 0)} className="px-4 py-12 text-center text-white/30">
                    {EmptyIcon && <EmptyIcon size={32} className="mx-auto mb-3 opacity-40" />}
                    <p className="font-mono text-[10px] uppercase tracking-widest">{emptyText}</p>
                  </td>
                </tr>
              ) : (
                groups.map((g) => {
                  const isCollapsed = collapsed.has(g.key);
                  return (
                    <React.Fragment key={g.key}>
                      {g.label && (
                        <tr className="border-b" style={{ borderColor: 'rgba(255,255,255,0.04)', background: 'rgba(255,255,255,0.02)' }}>
                          <td colSpan={visibleColumns.length + (rowActions ? 1 : 0)} className="px-4 py-2">
                            <button
                              onClick={() => toggleGroup(g.key)}
                              className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/60 hover:text-white"
                            >
                              {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                              <span>{g.label}</span>
                              <span className="text-white/30">({g.items.length})</span>
                            </button>
                          </td>
                        </tr>
                      )}
                      {!isCollapsed && g.items.map((item) => (
                        <tr
                          key={rowKey(item)}
                          className={`border-b last:border-0 hover:bg-white/[0.02] ${onRowClick ? 'cursor-pointer' : ''}`}
                          style={{ borderColor: 'rgba(255,255,255,0.04)' }}
                          onClick={onRowClick ? () => onRowClick(item) : undefined}
                        >
                          {visibleColumns.map((c) => (
                            <td
                              key={c.id}
                              className={`px-4 ${sizeCfg.py} ${sizeCfg.text} ${c.mono ? 'font-mono' : ''} ${c.align === 'right' ? 'text-right' : ''} ${c.muted ? 'text-white/50' : 'text-white/90'}`}
                              style={c.width ? { width: c.width } : undefined}
                            >
                              {c.render ? c.render(item) : item[c.id]}
                            </td>
                          ))}
                          {rowActions && (
                            <td className={`px-4 ${sizeCfg.py} text-right whitespace-nowrap`} onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-end gap-1">{rowActions(item)}</div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
